// Controlled flatpickr-based date input. Wraps `react-flatpickr` so
// every page in the dashboard speaks epoch-ms (matching the storage
// convention in tech-stack-conventions.md) without each form re-doing
// the YYYY-MM-DD <-> epoch conversion.
//
// Display format: MM/DD/YYYY (canonical project format, matches
// ECU_Database's flatpickr setup).
//
// Usage:
//   <DateInput value={courtDateEpoch} onChange={setCourtDateEpoch} />
//   <DateInput value={ts} onChange={setTs} withTime />

import { useMemo } from 'react';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import './DateInput.css';

export interface DateInputProps {
  value: number | null;
  onChange: (epochMs: number | null) => void;
  withTime?: boolean;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  /** Pass through native HTML id for label association. */
  id?: string;
  /** Extra class on the underlying input. */
  className?: string;
  /** Minimum selectable date as epoch ms (e.g. today for due-date fields). */
  minEpoch?: number;
  /** Maximum selectable date as epoch ms. */
  maxEpoch?: number;
}

export function DateInput(props: DateInputProps): JSX.Element {
  const {
    value, onChange, withTime = false, disabled = false,
    placeholder, required = false, id, className,
    minEpoch, maxEpoch,
  } = props;

  // react-flatpickr expects Date | Date[]; null/undefined means "no
  // default". Memoize to avoid recreating Date objects on every render
  // (which the underlying library treats as a value change).
  const flatpickrValue = useMemo(
    () => (value !== null && Number.isFinite(value) ? new Date(value) : undefined),
    [value],
  );

  const options = useMemo(
    () => ({
      dateFormat: withTime ? 'Y-m-d H:i' : 'Y-m-d',
      // Keep an alt-input visible so the user sees MM/DD/YYYY; the
      // hidden real input speaks the unambiguous Y-m-d to flatpickr.
      altInput: true,
      altFormat: withTime ? 'm/d/Y h:i K' : 'm/d/Y',
      enableTime: withTime,
      time_24hr: false,
      allowInput: true,
      disableMobile: true, // always show the styled calendar, not the
                           // native mobile picker
      ...(minEpoch !== undefined ? { minDate: new Date(minEpoch) } : {}),
      ...(maxEpoch !== undefined ? { maxDate: new Date(maxEpoch) } : {}),
    }),
    [withTime, minEpoch, maxEpoch],
  );

  const handleChange = (dates: Date[]): void => {
    if (dates.length === 0) {
      onChange(null);
      return;
    }
    onChange(dates[0]!.getTime());
  };

  // flatpickr 4.6.13 commits typed text SILENTLY when you click elsewhere on
  // the page: its outside-click handler calls setDate(text, false) — no
  // onChange — and the blur that follows then sees nothing new to report. The
  // box showed the new date while the form kept the old one; on Log Time that
  // read "End time must be after the start time" for 12:30 PM → 3:30 PM,
  // because the start was still today. Closing always follows that commit, so
  // reconcile there. A no-op whenever onChange already reported the date.
  //
  // Compared as the formatted string, not epoch ms: a timer-recorded start
  // carries seconds the picker doesn't show, and merely opening and closing
  // the calendar must not nudge it (and re-derive the duration from it).
  const handleClose = (
    dates: Date[],
    dateStr: string,
    fp: { formatDate: (d: Date, f: string) => string; config: { dateFormat: string } },
  ): void => {
    const shown = value === null ? '' : fp.formatDate(new Date(value), fp.config.dateFormat);
    if (dateStr === shown) return;
    onChange(dates.length === 0 ? null : dates[0]!.getTime());
  };

  return (
    <Flatpickr
      value={flatpickrValue}
      onChange={handleChange}
      onClose={handleClose}
      options={options}
      disabled={disabled}
      placeholder={placeholder ?? (withTime ? 'mm/dd/yyyy hh:mm' : 'mm/dd/yyyy')}
      required={required}
      id={id}
      className={`form-input date-input ${className ?? ''}`}
    />
  );
}
